/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import { forwardRef, memo, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { boxBorder, displayFlex, displayNone, flexCol, parseWidthHeight, pd, positionRelative, textColor } from "../../styles/general";
import { classNames } from "../../utils";
import TabContext from "./context";
const TabPanel = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  children,
  className,
  index,
  lazyLoading,
  style,
  ...props
}, reference) => {
  const {
    tabIndexState,
    direction
  } = useContext(TabContext);
  if (!reference) reference = useRef(null);
  const [isRendered, setIsRendered] = useState(false);
  const ref = useRef(null);
  const isVertical = direction === 'vertical';
  const isSelected = index === tabIndexState;
  const _TabPanelCSS = TabPanelCSS(isVertical, isSelected);
  useEffect(() => {
    if (isRendered) return;else if (index === tabIndexState) setIsRendered(true);
  }, [tabIndexState]);
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  return useMemo(() => {
    return jsx("div", {
      css: _TabPanelCSS,
      ref: ref,
      className: classNames('DGN-UI-Tab-Panel', className),
      style: style,
      ...props
    }, isSelected || !lazyLoading || isRendered ? children : null);
  }, [children, className, index, lazyLoading, props, style, isSelected, isRendered, tabIndexState]);
}));
const TabPanelCSS = (isVertical, isSelected) => css`
    ${displayFlex};
    ${flexCol};
    ${boxBorder};
    ${positionRelative};
    ${isSelected ? flexCol : displayNone};
    ${parseWidthHeight('100%', isVertical ? 300 : '100%')};
    ${textColor('text/main')};
    ${pd([0, isVertical ? 3 : 2.5])};
`;
TabPanel.defaultProps = {
  className: '',
  lazyLoading: false,
  style: {}
};
TabPanel.propTypes = {
  /** The content of the component. */
  children: PropTypes.node,
  /** Class for component. */
  className: PropTypes.string,
  /** Index received in position of TabHeaderButton. */
  index: PropTypes.number,
  /** Lazy loading mode. */
  lazyLoading: PropTypes.bool,
  /** Style inline of component. */
  style: PropTypes.object,
  /** Any props else. */
  props: PropTypes.any
};
export default TabPanel;